import ITimeZone from '@/types/ITimeZone';
import { Box } from '@chakra-ui/react';
import { Temporal } from 'temporal-polyfill';
import CalendarDay from './components/calendar-day/calendar-day.view';
import { useCalendar } from './month-calendar.controller';
import { IMonthCalendarEvent } from './types/IMonthCalendarEvent';

interface ITCalendarProps {
	onEventClick?: (event: IMonthCalendarEvent) => void;
	onRangeSelected?: (args: { start: Temporal.PlainDate; end: Temporal.PlainDate; timeZone: ITimeZone }) => void;
	events: Array<IMonthCalendarEvent>;
	isLoading?: boolean;
	locale?: string;
	month: number;
	timeZone: string;
	weekStartsOnMonday?: boolean;
	year: number;
}

export default function MonthCalendar(props: ITCalendarProps) {
	const locale = props.locale ?? navigator.language;
	const { days, eventMap, state, dispatch, handleEventClick } = useCalendar({ ...props, locale });

	return (
		<Box display="grid" gridTemplateRows="1fr" w="100%" h="100%" overflow="auto">
			<Box
				// Add a delay animation when opacity changes
				pointerEvents={props.isLoading ? 'none' : 'initial'}
				transition="all .2s"
				opacity={props.isLoading ? 0.5 : 1}
				overflow="auto"
				bg="white"
				display="grid"
				gridTemplateColumns={{ base: '1fr', sm: 'repeat(7, 1fr)' }}
				gridTemplateRows={{ base: 'repeat(42, 1fr)', sm: 'repeat(6, 1fr)' }}
				cursor={state.isSelecting ? 'grabbing' : 'default'}
				w="full"
				h="full"
				onMouseLeave={() => {
					dispatch({
						isSelecting: false,
						selectionEnd: null,
						selectionStart: null,
					});
				}}
			>
				{days.map((day) => {
					return (
						<CalendarDay
							events={eventMap[day.plainDate.toString()]}
							key={day.plainDate.toString()}
							isDragging={
								!!state.selectionStart &&
								!!state.selectionEnd &&
								Temporal.PlainDate.compare(day.plainDate, state.selectionStart) >= 0 &&
								Temporal.PlainDate.compare(day.plainDate, state.selectionEnd) <= 0
							}
							onMouseDown={() => {
								dispatch({ selectionEnd: day.plainDate, selectionStart: day.plainDate, isSelecting: true });
							}}
							onMouseEnter={() => {
								if (!state.isSelecting || !state.selectionEnd || !state.selectionStart) {
									return;
								}

								if (Temporal.PlainDate.compare(day.plainDate, state.selectionEnd) >= 0) {
									dispatch({ selectionEnd: day.plainDate });
								}
								if (
									Temporal.PlainDate.compare(day.plainDate, state.selectionEnd) <= 0 &&
									Temporal.PlainDate.compare(day.plainDate, state.selectionStart) >= 0
								) {
									dispatch({ selectionEnd: day.plainDate });
								}
								if (Temporal.PlainDate.compare(day.plainDate, state.selectionStart) <= 0) {
									dispatch({ selectionStart: day.plainDate });
								}
							}}
							onMouseUp={() => {
								if (!state.selectionStart || !state.selectionEnd) {
									return;
								}

								// The interval goes from the start of the first day to the end of the last day
								props.onRangeSelected?.({
									end: state.selectionEnd,
									start: state.selectionStart,
									timeZone: props.timeZone,
								});
								// Reset the state
								dispatch({ isSelecting: false, selectionStart: null, selectionEnd: null });
							}}
							plainDate={day.plainDate}
							locale={locale}
							timeZone={props.timeZone}
							onEventClick={handleEventClick}
						/>
					);
				})}
			</Box>
		</Box>
	);
}
